import { useState } from "react";
import { Spinner } from "@radix-ui/themes";
import formatPrice from "@/core/helpers/formatPrice";
import Button from "@/shared/ui/button/Button";
import { DEPOSIT_LIMIT } from "@/domains/bank/constants";
import { getFeeData } from "@/domains/bank/helpers";
import toast from "@/domains/toasts/helpers/toast";
import { useAtmQuery } from "@/domains/bank/hooks/useAtmQuery";
import { useDepositMutation } from "@/domains/bank/hooks/useDepositMutation";
import { ATMInput } from "@/domains/bank/components/BankATM/components/ATMInput";
import { BankATMBackButton } from "@/domains/bank/components/BankATM/components/BankATMBackButton";

export function BankATMDepositScreenContainer({
  onCancel,
  atmId,
  cashBalance,
}: {
  onCancel: () => void;
  atmId: number;
  cashBalance: number;
}) {
  const [depositAmount, setDepositAmount] = useState<number>(undefined);
  const depositMutation = useDepositMutation();

  const atmQuery = useAtmQuery(atmId);
  const feePercent = atmQuery.data?.fee.depositFeePercent;

  const { feeAmount, transferWithFeeAmount, maxAmountBeforeFee } = getFeeData({
    balance: cashBalance,
    feePercent,
    transferAmount: depositAmount,
    transferLimit: DEPOSIT_LIMIT,
    minFeeAmount: 100,
  });

  function handleAmountChange(value: string) {
    const int = Number.parseInt(value, 10);
    setDepositAmount(Math.min(int, DEPOSIT_LIMIT));
  }

  function handleMaxClick() {
    setDepositAmount(Math.min(maxAmountBeforeFee, DEPOSIT_LIMIT));
  }

  function handleWithdraw() {
    depositMutation.mutate(
      {
        depositAmount,
        atmId,
        filialId: null,
      },
      {
        onSuccess: () => {
          toast.success("Вы пополнили счет!");
        },
        onError: () => {
          toast.error("Не удалось пополнить счет");
        },
      },
    );
  }

  return (
    <div className="relative h-full flex flex-col">
      <BankATMBackButton onClick={onCancel} />

      <div className="flex-1 flex flex-col justify-between w-full max-w-[440px] mx-auto">
        <div>
          <h2 className="text-18 font-bold text-dark-800 mb-6 text-center">Внесение наличных</h2>

          <div className="mb-6">
            <ATMInput
              value={depositAmount?.toString()}
              onChange={(value) => handleAmountChange(value)}
              onMaxClick={() => handleMaxClick()}
              maxValue={maxAmountBeforeFee}
              className="mb-4"
            />

            <div className="flex items-center justify-between text-sbank-muted">
              <div className="text-normal text-15 text-dark-800">Комиссия:</div>{" "}
              <div className="text-14 text-[#186d27]">{formatPrice(feeAmount)} ₽</div>
            </div>
          </div>
        </div>

        {/* По текущей реализации комиссия снимается с введенной суммы */}
        {/* Сейчас так:  ввели 1000, списалась 1000, комиссия 100, будет получено 900 */}
        {/* Должно быть: ввели 1000, списалось 1100, комиссия 100, будет получено 1000 */}
        {/* Отметил красным, чтобы точно понять, что нужно исправить */}
        <div className="flex flex-col justify-end leading-tight text-right gap-5">
          <div>
            <div className="text-18 font-bold text-[#186d27] text-red">
              {formatPrice(transferWithFeeAmount)} ₽
            </div>
            <div className="text-sbank-muted text-normal text-15 text-red">
              Требуется внести с учетом комиссии
            </div>
          </div>

          <Button
            onClick={() => handleWithdraw()}
            disabled={!depositAmount || depositAmount <= 0}
            className="w-full h-[44px] shadow-[0px_6px_20px_0px_#186D2780]"
            data-mz-accent="green"
          >
            {depositMutation.isPending ? <Spinner className="size-5 animate-spin" /> : "Выполнить"}
          </Button>
        </div>
      </div>
    </div>
  );
}
